import React, { useState, useEffect, useRef } from 'react';
import UploadOrderDropdown from './UploadOrderDropdown';

const MaterialOrdersToolbar = ({
  search,
  onSearchChange,
  brands,
  niches,
  statuses = [],
  selectedBrands,
  setSelectedBrands,
  selectedNiches,
  setSelectedNiches,
  selectedStatuses,
  setSelectedStatuses,
  unpaidOnly,
  setUnpaidOnly,
  showBrandFilter,
  showNicheFilter,
  // UploadOrderDropdown handlers
  onCreateOrder,
  onAddOrder,
  onDownloadExample,
  onReferenceGuide,
  onUpdateOrderStatus,
  onManageSuppliers,
  onAddSeedsOrder,
  onDownloadSeedsExample,
  onUpdateSeedsOrder,
}) => {
  const [brandOpen,  setBrandOpen]  = useState(false);
  const [nicheOpen,  setNicheOpen]  = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const brandRef  = useRef(null);
  const nicheRef  = useRef(null);
  const statusRef = useRef(null);

  useEffect(() => {
    const handleOut = (e) => {
      if (brandRef.current  && !brandRef.current.contains(e.target))  setBrandOpen(false);
      if (nicheRef.current  && !nicheRef.current.contains(e.target))  setNicheOpen(false);
      if (statusRef.current && !statusRef.current.contains(e.target)) setStatusOpen(false);
    };
    document.addEventListener('mousedown', handleOut);
    return () => document.removeEventListener('mousedown', handleOut);
  }, []);

  const toggleBrand = (id) =>
    setSelectedBrands(prev => prev.includes(id) ? prev.filter(b => b !== id) : [...prev, id]);

  const toggleNiche = (id) =>
    setSelectedNiches(prev => prev.includes(id) ? prev.filter(n => n !== id) : [...prev, id]);

  const toggleStatus = (id) =>
    setSelectedStatuses(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);

  return (
    <div className="mo-toolbar">
      {/* Search */}
      <div className="mo-search-wrapper">
        <i className="fas fa-search mo-search-icon"></i>
        <input
          type="text"
          className="mo-search-input"
          placeholder="Search by order # or SKU..."
          value={search}
          onChange={e => onSearchChange(e.target.value)}
        />
        {search && (
          <button className="mo-search-clear" onClick={() => onSearchChange('')}>
            <i className="fas fa-times"></i>
          </button>
        )}
      </div>

      {/* Brand filter */}
      {showBrandFilter && (
        <div className="mo-filter-wrapper" ref={brandRef}>
          <button
            className={`mo-filter-btn${selectedBrands.length > 0 ? ' active' : ''}`}
            onClick={() => { setBrandOpen(o => !o); setNicheOpen(false); }}
          >
            <i className="fas fa-tag"></i>
            Brand
            {selectedBrands.length > 0 && (
              <span className="mo-filter-badge">{selectedBrands.length}</span>
            )}
            <i className={`fas fa-chevron-${brandOpen ? 'up' : 'down'} mo-chevron`}></i>
          </button>
          {brandOpen && (
            <div className="mo-dropdown">
              <div className="mo-dropdown-header">Filter by Brand</div>
              {brands.length === 0 ? (
                <div className="mo-dropdown-empty">No brands</div>
              ) : brands.map(b => (
                <label key={b.brandID} className="mo-dropdown-option">
                  <input
                    type="checkbox"
                    checked={selectedBrands.includes(String(b.brandID))}
                    onChange={() => toggleBrand(String(b.brandID))}
                  />
                  {b.brandName || b.brandID}
                </label>
              ))}
              {selectedBrands.length > 0 && (
                <button className="mo-dropdown-clear" onClick={() => setSelectedBrands([])}>
                  Clear
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Niche filter */}
      {showNicheFilter && (
        <div className="mo-filter-wrapper" ref={nicheRef}>
          <button
            className={`mo-filter-btn${selectedNiches.length > 0 ? ' active' : ''}`}
            onClick={() => { setNicheOpen(o => !o); setBrandOpen(false); }}
          >
            <i className="fas fa-layer-group"></i>
            Niche
            {selectedNiches.length > 0 && (
              <span className="mo-filter-badge">{selectedNiches.length}</span>
            )}
            <i className={`fas fa-chevron-${nicheOpen ? 'up' : 'down'} mo-chevron`}></i>
          </button>
          {nicheOpen && (
            <div className="mo-dropdown">
              <div className="mo-dropdown-header">Filter by Niche</div>
              {niches.length === 0 ? (
                <div className="mo-dropdown-empty">No niches</div>
              ) : niches.map(n => (
                <label key={n.niche_category_id} className="mo-dropdown-option">
                  <input
                    type="checkbox"
                    checked={selectedNiches.includes(String(n.niche_category_id))}
                    onChange={() => toggleNiche(String(n.niche_category_id))}
                  />
                  {n.niche_name}
                </label>
              ))}
              {selectedNiches.length > 0 && (
                <button className="mo-dropdown-clear" onClick={() => setSelectedNiches([])}>
                  Clear
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Status filter */}
      <div className="mo-filter-wrapper" ref={statusRef}>
        <button
          className={`mo-filter-btn${selectedStatuses.length > 0 ? ' active' : ''}`}
          onClick={() => { setStatusOpen(o => !o); setBrandOpen(false); setNicheOpen(false); }}
        >
          <i className="fas fa-circle-half-stroke"></i>
          Status
          {selectedStatuses.length > 0 && (
            <span className="mo-filter-badge">{selectedStatuses.length}</span>
          )}
          <i className={`fas fa-chevron-${statusOpen ? 'up' : 'down'} mo-chevron`}></i>
        </button>
        {statusOpen && (
          <div className="mo-dropdown">
            <div className="mo-dropdown-header">Filter by Status</div>
            {statuses.map(s => (
              <label key={s.statusID} className="mo-dropdown-option">
                <input
                  type="checkbox"
                  checked={selectedStatuses.includes(s.statusID)}
                  onChange={() => toggleStatus(s.statusID)}
                />
                {s.statusID} — {s.status_name}
              </label>
            ))}
            {selectedStatuses.length > 0 && (
              <button className="mo-dropdown-clear" onClick={() => setSelectedStatuses([])}>
                Clear
              </button>
            )}
          </div>
        )}
      </div>

      {/* Unpaid-only toggle */}
      <button
        className={`mo-filter-btn${unpaidOnly ? ' active' : ''}`}
        onClick={() => setUnpaidOnly(v => !v)}
        title="Show only orders that are not fully paid"
      >
        <i className="fas fa-dollar-sign"></i>
        Unpaid
      </button>

      {/* Upload / actions dropdown */}
      <div style={{ marginLeft: 'auto' }}>
        <UploadOrderDropdown
          onCreateOrder={onCreateOrder}
          onAddSmallBagsOrder={onAddOrder}
          onDownloadExample={onDownloadExample}
          onReferenceGuide={onReferenceGuide}
          onUpdateOrderStatus={onUpdateOrderStatus}
          onManageSuppliers={onManageSuppliers}
          onAddSeedsOrder={onAddSeedsOrder}
          onDownloadSeedsExample={onDownloadSeedsExample}
          onUpdateSeedsOrder={onUpdateSeedsOrder}
        />
      </div>
    </div>
  );
};

export default MaterialOrdersToolbar;
